import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  MenuItem,
  IconButton,
} from '@material-ui/core';
import PersonOutline from '@material-ui/icons/PersonOutline';
import ThumbUpOutlined from '@material-ui/icons/ThumbUpOutlined';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { Link } from '@backstage/core-components';
import { SectionIntro } from '@univerus/plugin-products';
import { useHomeStyles } from './homeStyles';
import { ScrollTrackControls, useScrollTrack } from './ScrollTrack';
import { displayDate, internalPath } from './contentModel';
import releaseFeed from './data/releases.json';

// Real release notes, captured from Hub TechDocs (see data/releases.json).
// Meeting Minutes is the only product publishing them so far; others join
// as they publish docs/customer/releases/ the same way.
const releaseList = [...releaseFeed.releases].sort((x, y) =>
  y.date.localeCompare(x.date),
);

// Release filters, recognition submissions and voting are not connected.
export function CommunitySections() {
  const classes = useHomeStyles();
  const releases = useScrollTrack();
  return (
    <>
      <section
        className={classes.releasesSection}
        aria-labelledby="releases-title"
      >
        <div className={classes.sectionHead}>
          <SectionIntro
            size="section"
            id="releases-title"
            title="Software releases"
          >
            What is new in each product.
          </SectionIntro>
          <nav className={classes.sectionLinks} aria-label="More releases">
            <Link to="/releases">All releases →</Link>
            <Link to="/products">Explore products →</Link>
          </nav>
        </div>
        <div
          className={classes.releaseFilters}
          role="group"
          aria-label="Release filters"
          aria-describedby="release-status"
        >
          <TextField
            disabled
            id="home-release-search"
            label="Search releases"
            variant="outlined"
            size="small"
          />
          <TextField
            disabled
            select
            id="home-release-bu"
            label="Business unit"
            value="all"
            variant="outlined"
            size="small"
          >
            <MenuItem value="all">All business units</MenuItem>
          </TextField>
          <TextField
            disabled
            select
            id="home-release-product"
            label="Product"
            value="all"
            variant="outlined"
            size="small"
          >
            <MenuItem value="all">All products</MenuItem>
          </TextField>
        </div>
        <p className={classes.previewNote} id="release-status">
          <strong>
            Meeting Minutes is the first product publishing release notes.
          </strong>{' '}
          Captured from Hub on {displayDate(releaseFeed.capturedAt)}; other
          products appear here once they publish notes the same way. Search and
          filters are not wired up yet.
        </p>
        <ScrollTrackControls
          label={`Browse ${releaseList.length} releases`}
          noun="releases"
          trackId="home-release-track"
          edges={releases.edges}
          step={releases.step}
        />
        <div
          id="home-release-track"
          ref={releases.ref}
          className={`${classes.scrollTrack} ${classes.releaseTrack}`}
          role="region"
          aria-label="Software releases"
        >
          {releaseList.map(release => {
            const name = `${release.product} ${release.version}`;
            return (
              <article key={release.url} className={classes.releaseCard}>
                <Typography className={classes.releasePortfolio}>
                  {release.owner}
                </Typography>
                <Typography component="h3" className={classes.releaseProduct}>
                  {name}
                </Typography>
                <Typography className={classes.releaseSummary}>
                  <strong>Highlights.</strong> {release.highlight}
                </Typography>
                <div className={classes.releaseActions}>
                  <time className={classes.muted} dateTime={release.date}>
                    {displayDate(release.date)}
                  </time>
                  <Link
                    className={classes.releaseReadMore}
                    to={internalPath(release.url) || '/docs'}
                    aria-label={`Read the ${name} release notes`}
                  >
                    Read more <ChevronRight aria-hidden="true" />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>
      {/*
        ON HOLD (8 Oct 2026): Shout-outs and Quick poll are a UI-only draft
        based on Brad's feedback. Nothing is stored, sent or counted, and no
        recognition or voting source exists yet. Parked for a future date;
        not part of the EVP alpha. Gate or remove before merging to main.
      */}
      <div className={classes.community}>
        <section aria-labelledby="shoutouts-title">
          <div className={classes.sectionHead}>
            <SectionIntro
              size="section"
              id="shoutouts-title"
              title="Shout-outs"
            >
              Thanks across the team.
            </SectionIntro>
          </div>
          <p className={classes.previewNote}>
            <strong>Layout preview</strong> · Recognition source not connected
          </p>
          <div className={classes.recognitionCards}>
            {[
              'Thank you for stepping in to support the team. Your care and collaboration made a real difference.',
              'Thank you for going the extra mile to help a teammate. Sharing your knowledge keeps us moving forward.',
            ].map((message, index) => (
              <article
                className={classes.recognitionCard}
                key={message}
                aria-label={`Recognition layout preview ${index + 1}`}
              >
                <div className={classes.recognitionByline}>
                  <strong>Colleague name</strong> recognized{' '}
                  <strong>Teammate name</strong>
                  <span>Example recognition</span>
                </div>
                <div className={classes.recognitionPeople} aria-hidden="true">
                  <span>
                    <PersonOutline />
                  </span>
                  <b>{index === 0 ? '🎉' : '👏'}</b>
                  <ChevronRight />
                  <span>
                    <PersonOutline />
                  </span>
                </div>
                <p>{message}</p>
                <div className={classes.recognitionReaction}>
                  <IconButton
                    disabled
                    size="small"
                    aria-label="Like recognition preview"
                  >
                    <ThumbUpOutlined />
                  </IconButton>
                  <span>—</span>
                </div>
              </article>
            ))}
          </div>
          <div className={classes.recognitionActions}>
            <Button variant="contained" color="secondary" disabled>
              Give praise
            </Button>
            <div role="group" aria-label="Recognition pagination preview">
              <IconButton
                disabled
                size="small"
                aria-label="Previous shout-outs"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton disabled size="small" aria-label="Next shout-outs">
                <ChevronRight />
              </IconButton>
            </div>
          </div>
        </section>
        <section aria-labelledby="poll-title">
          <div className={classes.sectionHead}>
            <SectionIntro size="section" id="poll-title" title="Quick poll" />
          </div>
          <div className={classes.poll}>
            <div className={classes.pollHeader}>
              Preview · Voting not connected
            </div>
            <div className={classes.pollBody}>
              <Typography
                component="h3"
                id="poll-question"
                className={classes.feedTitle}
              >
                How is your Univerus Central experience?
              </Typography>
              <RadioGroup aria-labelledby="poll-question" value="">
                {[
                  'Very satisfied',
                  'Satisfied',
                  'Neutral',
                  'Could be better',
                ].map(label => (
                  <FormControlLabel
                    key={label}
                    value={label}
                    control={<Radio disabled color="primary" />}
                    classes={{ label: classes.pollLabel }}
                    label={label}
                  />
                ))}
              </RadioGroup>
              <Button disabled variant="contained">
                Vote
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
